// RBAC del campus · espejo de los permisos ATÓMICOS del backend
// (backend/app/permissions.py). F3 Admin v2 · sprint feat/campus-rbac-staff.
//
// FUENTE DE LA VERDAD = el backend. El frontend NO recalcula qué permisos
// concede cada rol: recibe el array `permissions[]` del rol efectivo en
// `GET /api/me/profile` y lo propaga en la identidad (ver auth-context). Este
// módulo solo aporta:
//   1. Las CONSTANTES de permiso (strings canónicos) para que el nav y los
//      gates referencien `PERMISSIONS.usuariosView` en vez de literales sueltos
//      (evita typos · grep-able · un único sitio que tocar si cambia el nombre).
//   2. El tipo `CampusRole` (los 6 roles del enum `campus.users.role`).
//   3. El helper `hasPermission(permissions, perm)` (deny-by-default sobre el
//      array que vino del backend).
//
// IMPORTANTE: si se añade/renombra un permiso en el backend hay que reflejarlo
// aquí. Como el backend manda el array, un permiso que falte en estas
// constantes simplemente no se referenciaría desde el front (no rompe el
// gating real, que es server-side); pero conviene mantenerlos sincronizados.

/** Roles válidos · espejo de `VALID_ROLES` (migración 068). */
export type CampusRole = 'owner' | 'admin' | 'soporte' | 'tutor' | 'staff' | 'student';

/** Permisos atómicos · cadenas canónicas idénticas a las constantes `P_*`
 *  del backend (`backend/app/permissions.py`). */
export const PERMISSIONS = {
  analiticaView: 'admin.analitica.view',
  usuariosView: 'admin.usuarios.view',
  usuariosActions: 'admin.usuarios.actions',
  // El cambio de rol se gobierna por `rolesManage` (solo owner) · no hay un
  // permiso separado role_change (F-RBAC-ROLECHANGE-001 · 2026-06-15).
  entitlementsWrite: 'admin.entitlements.write',
  contenidoWrite: 'admin.contenido.write',
  licenciasView: 'admin.licencias.view',
  // APPROVE (soporte SÍ) = verify-IB + sync GHL. MANAGE (solo owner/admin) =
  // acciones destructivas/emisoras (revoke/archive/crear/cupo). F-RGPD-CAMPUS-LICENSING-002.
  licenciasApprove: 'admin.licencias.approve',
  licenciasManage: 'admin.licencias.manage',
  actividadView: 'admin.actividad.view',
  integraciones: 'admin.integraciones',
  exportPii: 'admin.export_pii',
  rolesManage: 'admin.roles.manage',
  impersonate: 'admin.impersonate',
  accessAsStudent: 'campus.access_as_student',
  // Buzón de sugerencias (feat/buzon-nav-usuarios · 2026-06-15). VIEW = leer la
  // bandeja (owner/admin/soporte). MANAGE = cambiar el status (owner/admin). El
  // ENVÍO de sugerencias NO necesita permiso: lo hace cualquier autenticado.
  sugerenciasView: 'admin.sugerencias.view',
  sugerenciasManage: 'admin.sugerencias.manage',
  // Enlaces (mig 072). Editar URLs de venta/checkout = destructivo → owner/admin
  // (NO soporte · F-ENLACES-002). La VISTA del panel usa `integraciones`.
  enlacesManage: 'admin.enlaces.manage',
  // Robotea Lab › Informes (decisión Jaime 2026-08-29). SOLO owner — ni
  // siquiera admin lo ve (ver backend/app/permissions.py, P_ROBOTEA_LAB_VIEW).
  roboteaLabView: 'robotea_lab.view',
} as const;

/** Tipo de un permiso atómico (union de los valores de `PERMISSIONS`). */
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * ¿El conjunto de permisos del usuario (el array que vino del backend en
 * `/api/me/profile`) concede `perm`? Deny-by-default: si `permissions` es null
 * o no incluye el permiso, devuelve false.
 *
 * NO es la decisión de seguridad real (esa es server-side en cada endpoint vía
 * `require_permission`); aquí solo decide qué se MUESTRA en la UI.
 */
export function hasPermission(
  permissions: readonly string[] | null | undefined,
  perm: Permission,
): boolean {
  if (!permissions) return false;
  return permissions.includes(perm);
}

/**
 * ¿El usuario tiene AL MENOS UNO de los permisos? Útil para mostrar una
 * cabecera/sección que agrupa varias sub-vistas con permisos distintos
 * (ej. mostrar "Panel admin" si tiene cualquier permiso de admin).
 */
export function hasAnyPermission(
  permissions: readonly string[] | null | undefined,
  perms: readonly Permission[],
): boolean {
  if (!permissions) return false;
  return perms.some((p) => permissions.includes(p));
}
